import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import * as listsApi from '../endpoints/lists';
import type { ListListsParams, ListItemsParams } from '../endpoints/lists';
import type {
  ItemStatus,
  List,
  ListMember,
  PaginatedListItemResponse,
  PaginatedListResponse,
} from '@/features/lists/types';

/**
 * Query keys for lists
 */
export const LISTS_QUERY_KEYS = {
  all: ['lists'] as const,
  lists: () => [...LISTS_QUERY_KEYS.all, 'list'] as const,
  list: (id: string) => [...LISTS_QUERY_KEYS.lists(), id] as const,
  items: (listId: string) => [...LISTS_QUERY_KEYS.all, 'items', listId] as const,
  members: (listId: string) => [...LISTS_QUERY_KEYS.all, 'members', listId] as const,
  itemStatus: (itemType: string, itemId: string) =>
    [...LISTS_QUERY_KEYS.all, 'item-status', itemType, itemId] as const,
} as const;

/**
 * Domain-oriented query options for list operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for listing the current user's lists (paginated)
 *
 * @param params - Query parameters for pagination and sorting
 * @returns Query options for the lists collection
 *
 * @example
 * ```typescript
 * const options = listsQueryOptions();
 * const optionsWithSort = listsQueryOptions({ sort_by: 'name', sort_order: 'asc' });
 * ```
 */
export const listsQueryOptions = (params: ListListsParams = {}) =>
  queryOptions({
    queryKey: [...LISTS_QUERY_KEYS.lists(), params] as const,
    queryFn: async (): Promise<PaginatedListResponse> => {
      return listsApi.getLists(params);
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching a single list by ID
 *
 * @param id - List ID
 * @returns Query options for the list detail
 *
 * @example
 * ```typescript
 * const options = listDetailQueryOptions('list-123');
 * ```
 */
export const listDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: LISTS_QUERY_KEYS.list(id),
    queryFn: async (): Promise<List> => {
      return listsApi.getList(id);
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching paginated items in a list
 *
 * @param listId - List ID
 * @param params - Query parameters for pagination and sorting
 * @returns Query options for list items
 *
 * @example
 * ```typescript
 * const options = listItemsQueryOptions('list-123');
 * const optionsSorted = listItemsQueryOptions('list-123', { sort_by: 'name' });
 * ```
 */
export const listItemsQueryOptions = (listId: string, params: ListItemsParams = {}) =>
  queryOptions({
    queryKey: [...LISTS_QUERY_KEYS.items(listId), params] as const,
    queryFn: async (): Promise<PaginatedListItemResponse> => {
      return listsApi.getListItems(listId, params);
    },
    enabled: !!listId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Infinite query options for paginated list items (infinite scroll)
 *
 * @param listId - List ID
 * @param params - Query parameters for sorting (page is managed by infinite query)
 * @returns Infinite query options for list items
 *
 * @example
 * ```typescript
 * const options = listItemsInfiniteQueryOptions('list-123');
 * ```
 */
export const listItemsInfiniteQueryOptions = (
  listId: string,
  params: Omit<ListItemsParams, 'page'> = {}
) =>
  infiniteQueryOptions({
    queryKey: [...LISTS_QUERY_KEYS.items(listId), 'infinite', params] as const,
    queryFn: async ({ pageParam }): Promise<PaginatedListItemResponse> => {
      return listsApi.getListItems(listId, {
        ...params,
        page: pageParam,
        per_page: params.per_page ?? 20,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: !!listId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching members of a list
 *
 * @param listId - List ID
 * @returns Query options for list members
 *
 * @example
 * ```typescript
 * const options = listMembersQueryOptions('list-123');
 * ```
 */
export const listMembersQueryOptions = (listId: string) =>
  queryOptions({
    queryKey: LISTS_QUERY_KEYS.members(listId),
    queryFn: async (): Promise<ListMember[]> => {
      return listsApi.getListMembers(listId);
    },
    enabled: !!listId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for checking which lists contain a given item
 * Useful for checkbox UIs when saving items to lists
 *
 * @param itemType - Type of item ('menu_item' or 'business')
 * @param itemId - ID of the item to check
 * @returns Query options for item status
 *
 * @example
 * ```typescript
 * const options = itemStatusQueryOptions('menu_item', 'item-456');
 * ```
 */
export const itemStatusQueryOptions = (itemType: string, itemId: string) =>
  queryOptions({
    queryKey: LISTS_QUERY_KEYS.itemStatus(itemType, itemId),
    queryFn: async (): Promise<ItemStatus> => {
      return listsApi.getItemStatus(itemType, itemId);
    },
    enabled: !!itemType && !!itemId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });
